import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import notificationsService from '../../../services/notificationsService';

const NotificationHistory = ({ notifications, onNotificationSent, onNotificationDeleted }) => {
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [deliveryStats, setDeliveryStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'sent', label: 'Sent' },
    { value: 'failed', label: 'Failed' }
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'general', label: 'General' },
    { value: 'event', label: 'Event' },
    { value: 'release', label: 'Release' },
    { value: 'merchandise', label: 'Merchandise' }
  ];

  // Filter notifications based on selected filters
  const filteredNotifications = notifications?.filter(notification => {
    const statusMatch = filterStatus === 'all' || notification?.status === filterStatus;
    const typeMatch = filterType === 'all' || notification?.notification_type === filterType;
    return statusMatch && typeMatch;
  }) || [];

  // Handle sending a draft notification
  const handleSendNotification = async (notificationId) => {
    setLoading(true);
    try {
      await onNotificationSent?.(notificationId);
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting a notification
  const handleDeleteNotification = async (notificationId) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    setLoading(true);
    try {
      const result = await notificationsService?.deleteNotification(notificationId);
      if (result?.error) throw new Error(result.error);
      
      onNotificationDeleted?.(notificationId);
    } catch (error) {
      alert(error?.message || 'Failed to delete notification');
    } finally {
      setLoading(false);
    }
  };

  // Load delivery stats for a notification
  const loadDeliveryStats = async (notificationId) => {
    try {
      const result = await notificationsService?.getDeliveryStats(notificationId);
      if (result?.error) throw new Error(result.error);
      
      setDeliveryStats(prev => ({
        ...prev,
        [notificationId]: result?.data
      }));
    } catch (error) {
      console.error('Failed to load delivery stats:', error);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      sent: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    return styles?.[status] || 'bg-gray-100 text-gray-800';
  };

  // Get type icon
  const getTypeIcon = (type) => {
    const icons = {
      general: 'MessageCircle',
      event: 'Calendar',
      release: 'Music',
      merchandise: 'ShoppingBag'
    };
    return icons?.[type] || 'MessageCircle';
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Notification History</h2>
            <p className="text-sm text-text-secondary">
              View and manage your sent notifications
            </p>
          </div>
          
          <div className="text-sm text-text-secondary">
            {filteredNotifications?.length} notification(s)
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-lg bg-surface text-text-primary text-sm"
          >
            {statusOptions?.map(option => (
              <option key={option?.value} value={option?.value}>
                {option?.label}
              </option>
            ))}
          </select>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-lg bg-surface text-text-primary text-sm"
          >
            {typeOptions?.map(option => (
              <option key={option?.value} value={option?.value}>
                {option?.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Notifications List */}
      {filteredNotifications?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Bell" size={48} className="text-text-secondary mx-auto mb-4" />
          <p className="text-text-secondary">No notifications found</p>
          <p className="text-sm text-text-secondary mt-2">
            {filterStatus === 'all' && filterType === 'all' ?'Create your first notification to get started' :'Try adjusting your filters'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredNotifications?.map((notification) => (
            <div key={notification?.id} className="border border-border rounded-lg p-4 hover:bg-surface/80 transition-colors bg-surface">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Header */}
                  <div className="flex items-center space-x-3 mb-2">
                    <Icon 
                      name={getTypeIcon(notification?.notification_type)} 
                      size={16} 
                      className="text-text-secondary"
                    />
                    <h3 className="font-medium text-text-primary">
                      {notification?.title}
                    </h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(notification?.status)}`}>
                      {notification?.status?.charAt(0)?.toUpperCase() + notification?.status?.slice(1)}
                    </span>
                  </div>

                  {/* Message */}
                  <p className="text-text-secondary mb-3 line-clamp-2">
                    {notification?.message}
                  </p>

                  {/* Meta Info */}
                  <div className="flex items-center space-x-4 text-sm text-text-secondary">
                    <div className="flex items-center space-x-1">
                      <Icon name="Clock" size={14} />
                      <span>Created: {formatDate(notification?.created_at)}</span>
                    </div>
                    
                    {notification?.sent_at && (
                      <div className="flex items-center space-x-1">
                        <Icon name="Send" size={14} />
                        <span>Sent: {formatDate(notification?.sent_at)}</span>
                      </div>
                    )}
                    
                    {notification?.scheduled_for && (
                      <div className="flex items-center space-x-1">
                        <Icon name="Calendar" size={14} />
                        <span>Scheduled: {formatDate(notification?.scheduled_for)}</span>
                      </div>
                    )}
                  </div>

                  {/* Delivery Stats */}
                  {notification?.status === 'sent' && deliveryStats?.[notification?.id] && (
                    <div className="mt-3 p-3 bg-background rounded-lg border border-border">
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="font-medium text-text-primary">
                            {deliveryStats?.[notification?.id]?.total || 0}
                          </div>
                          <div className="text-text-secondary">Total</div>
                        </div>
                        <div>
                          <div className="font-medium text-green-600">
                            {deliveryStats?.[notification?.id]?.delivered || 0}
                          </div>
                          <div className="text-text-secondary">Delivered</div>
                        </div>
                        <div>
                          <div className="font-medium text-red-600">
                            {deliveryStats?.[notification?.id]?.failed || 0}
                          </div>
                          <div className="text-text-secondary">Failed</div>
                        </div>
                        <div>
                          <div className="font-medium text-blue-600">
                            {deliveryStats?.[notification?.id]?.deliveryRate || 0}%
                          </div>
                          <div className="text-text-secondary">Success Rate</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="ml-4 flex flex-col space-y-2">
                  {notification?.status === 'draft' && (
                    <Button
                      size="sm"
                      onClick={() => handleSendNotification(notification?.id)}
                      disabled={loading}
                      iconName="Send"
                      iconSize={14}
                    >
                      Send Now
                    </Button>
                  )}

                  {notification?.status === 'sent' && !deliveryStats?.[notification?.id] && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadDeliveryStats(notification?.id)}
                      iconName="BarChart3"
                      iconSize={14}
                    >
                      View Stats
                    </Button>
                  )}

                  {(notification?.status === 'draft' || notification?.status === 'failed') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteNotification(notification?.id)}
                      disabled={loading}
                      iconName="Trash2"
                      iconSize={14}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationHistory;